/**
 * Phase 6b2 PR 2 — `useTeamActions` hook.
 *
 * Wraps the four team write operations behind a single `useAuthFetch`-
 * backed API. Each method returns the parsed JSON body on 2xx and
 * throws an Error with the server-provided message on non-2xx.
 *
 * Endpoints (all server-stamp `req.authUser.id`):
 *
 *   - `POST /api/team/create`   — T6 create team
 *       body: { name, sport?, season? }
 *   - `POST /api/team/join`     — T2 accept invite (paste-and-join)
 *       body: { inviteCode }
 *   - `POST /api/team/leave`    — T8 athlete leaves team (NEW in 6b2)
 *       body: { teamId? }       optional — defaults to caller's active team
 *   - `POST /api/team/remove`   — T7 trainer removes athlete (NEW in 6b2)
 *       body: { teamId, athleteId }
 *
 * Per safety gate #6, the hook NEVER passes a client-supplied `user_id`
 * / `giver_user_id` / `trainer_id`. The server is the source of truth
 * for "who is calling"; callers identify other parties (e.g., the
 * target athlete in T7) by id only.
 *
 * Errors flow through unwrapped — the caller decides how to surface
 * (toast, in-modal danger chip, retry banner). The hook itself stays
 * presentational-free.
 */

import { useCallback } from 'react';
import { canonicalOrigin } from '../lib/apiBase';
import { useAuthFetch } from './useAuthFetch';

export interface CreateTeamInput {
  name: string;
  sport?: string | null;
  season?: string | null;
}

export interface JoinTeamInput {
  /** Raw invite code (e.g., "TEST1-XCAV"). The caller is responsible
   *  for parsing `wai.run/join/<code>` URLs down to the bare code
   *  before calling. */
  inviteCode: string;
}

export interface RemoveAthleteInput {
  teamId: string;
  athleteId: string;
}

export interface CreatedTeam {
  id: string;
  name: string;
  sport: string | null;
  season: string | null;
  invite_code: string | null;
  trainer_id: string;
}

export interface JoinedTeam {
  id: string;
  name: string;
  season: string | null;
  inviteCode: string | null;
}

/** P7(a) — the coach's invite-batch result (server dedupes + skips
 *  already-active members). */
export interface InviteAthletesResult {
  invited: number;
  skipped: number;
}

export interface UseTeamActionsResult {
  createTeam: (input: CreateTeamInput) => Promise<CreatedTeam>;
  joinTeam: (input: JoinTeamInput) => Promise<JoinedTeam>;
  /** teamId is optional; the server resolves caller's active team
   *  when omitted (single-team parity). */
  leaveTeam: (teamId?: string) => Promise<void>;
  removeAthlete: (input: RemoveAthleteInput) => Promise<void>;
  /** P7(a) — invite N athletes by email (pending rows the athletes
   *  claim on sign-in). Server is trainer-gated (coach_provisioning_v1). */
  inviteAthletes: (teamId: string, emails: string[]) => Promise<InviteAthletesResult>;
  /** P7(a) — revoke a pending email invite by its membership-row id. */
  revokeInvite: (inviteId: string) => Promise<void>;
}

interface ErrorBody {
  error?: string;
  code?: string;
}

/** B205 leftovers — thrown by `joinTeam` so the caller can distinguish a
 *  guardian-consent hold (`code: 'guardian_consent_required'`, the
 *  `coppaHoldResponse` shape) from any other failure, the same `code` field
 *  `InviteActionError` carries for accept/decline (`useMyTeamInvites.ts`). */
export interface TeamActionError extends Error {
  code?: string;
}

async function readJsonError(res: Response): Promise<never> {
  let body: ErrorBody = {};
  try {
    body = (await res.json()) as ErrorBody;
  } catch {
    /* response wasn't JSON; fall through to status text. */
  }
  const msg = body?.error ?? `${res.status} ${res.statusText || 'Request failed'}`;
  const err = new Error(msg) as TeamActionError;
  err.code = body?.code;
  throw err;
}

export function useTeamActions(): UseTeamActionsResult {
  const fetchAuthed = useAuthFetch();

  const createTeam = useCallback<UseTeamActionsResult['createTeam']>(
    async (input) => {
      const res = await fetchAuthed('/api/team/create', {
        method: 'POST',
        body: JSON.stringify({
          name: input.name,
          sport: input.sport ?? undefined,
          season: input.season ?? undefined,
        }),
      });
      if (!res.ok) await readJsonError(res);
      const json = (await res.json()) as { team: CreatedTeam };
      return json.team;
    },
    [fetchAuthed],
  );

  const joinTeam = useCallback<UseTeamActionsResult['joinTeam']>(
    async (input) => {
      const res = await fetchAuthed('/api/team/join', {
        method: 'POST',
        body: JSON.stringify({ inviteCode: input.inviteCode }),
      });
      if (!res.ok) await readJsonError(res);
      const json = (await res.json()) as { team: JoinedTeam };
      return json.team;
    },
    [fetchAuthed],
  );

  const leaveTeam = useCallback<UseTeamActionsResult['leaveTeam']>(
    async (teamId) => {
      const res = await fetchAuthed('/api/team/leave', {
        method: 'POST',
        body: JSON.stringify(teamId ? { teamId } : {}),
      });
      if (!res.ok) await readJsonError(res);
    },
    [fetchAuthed],
  );

  const removeAthlete = useCallback<UseTeamActionsResult['removeAthlete']>(
    async (input) => {
      const res = await fetchAuthed('/api/team/remove', {
        method: 'POST',
        body: JSON.stringify({
          teamId: input.teamId,
          athleteId: input.athleteId,
        }),
      });
      if (!res.ok) await readJsonError(res);
    },
    [fetchAuthed],
  );

  const inviteAthletes = useCallback<UseTeamActionsResult['inviteAthletes']>(
    async (teamId, emails) => {
      const res = await fetchAuthed('/api/team/invites', {
        method: 'POST',
        body: JSON.stringify({ teamId, emails }),
      });
      if (!res.ok) await readJsonError(res);
      const json = (await res.json()) as InviteAthletesResult;
      return { invited: json.invited ?? 0, skipped: json.skipped ?? 0 };
    },
    [fetchAuthed],
  );

  const revokeInvite = useCallback<UseTeamActionsResult['revokeInvite']>(
    async (inviteId) => {
      const res = await fetchAuthed(
        `/api/team/invites/${encodeURIComponent(inviteId)}/revoke`,
        { method: 'POST' },
      );
      // F-205 — a 404 means the invite is already gone (revoked in another
      // tab, expired, or just accepted). The goal of a revoke is exactly
      // "this pending invite no longer exists" — already true — so treat it
      // as an idempotent success instead of surfacing a raw "Not found" in a
      // stuck confirm dialog. The caller's success path then refetches and
      // the ghost row clears.
      if (res.status === 404) return;
      if (!res.ok) await readJsonError(res);
    },
    [fetchAuthed],
  );

  return { createTeam, joinTeam, leaveTeam, removeAthlete, inviteAthletes, revokeInvite };
}

/** Helper exported for tests + the JoinTeamModal: parse a pasted
 *  invite URL down to the bare invite code. Accepts both the URL
 *  shape used by the current React build (`<origin>/v2/landing?join=<code>`)
 *  AND the legacy `wai.run/join/<code>` shape that vanilla used —
 *  athletes may still have old invite links in their messages.
 *  Bare codes pass through unchanged after uppercasing.
 *
 *  Examples:
 *    "https://wai-sports.vercel.app/v2/landing?join=TEST1-XCAV" → "TEST1-XCAV"
 *    "wai.run/join/TEST1-XCAV"                                  → "TEST1-XCAV"
 *    "https://wai.run/join/test1-xcav"                          → "TEST1-XCAV"
 *    "test1-xcav"                                               → "TEST1-XCAV"
 *    " test1-xcav "                                             → "TEST1-XCAV" (trimmed)
 *    ""                                                         → null
 *
 *  B444 (rulings 2026-10-21 item 12: "team join code: letters and digits only
 *  at the code's length") — whichever way the code arrives, it must have the
 *  SHAPE the server makes: `generateInviteCode` in `api/index.js` builds
 *  `<up to 8 letters or digits>-<4 letters or digits>` (a rotated code ends in
 *  8). So a code is letters and digits with at most one hyphen between a part
 *  of up to 8 and a part of up to 8, at most 17 characters; anything else
 *  (an underscore, an accent, a second hyphen, 10,000 characters) returns
 *  null and never reaches the server's lookup. A real generated code passing
 *  this is pinned by `useTeamActions.test.ts` against the generator's own text.
 */
const INVITE_CODE_SHAPE = /^[A-Za-z0-9]{0,8}-?[A-Za-z0-9]{1,8}$/;

export function parseInviteCode(raw: string): string | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return null;
  const shaped = (code: string): string | null =>
    INVITE_CODE_SHAPE.test(code) ? code.toUpperCase() : null;
  // Try the canonical query-param form first: `?join=<code>`. This is
  // the format `buildInviteLink` produces in PR-4-fix.
  const queryMatch = trimmed.match(/[?&]join=([A-Za-z0-9_-]+)/);
  if (queryMatch) return shaped(queryMatch[1]);
  // Legacy `/join/<code>` URL shape — vanilla parity + still valid
  // for old invite links circulating in chat threads / SMS.
  const pathMatch = trimmed.match(/\/join\/([A-Za-z0-9_-]+)/);
  if (pathMatch) return shaped(pathMatch[1]);
  // Bare code form. Reject anything with spaces or other suspicious characters.
  return shaped(trimmed);
}

/** Build a real invite URL on the deployed origin (Bug-4 fix).
 *
 *  Returns the canonical shape `${origin}/v2/landing?join=<code>` so:
 *    - The URL actually resolves (no SSL error from the aspirational
 *      `wai.run` domain that was never DNS-configured)
 *    - Signed-in athletes who click the link land on /v2/landing,
 *      which detects `?join=` and forwards them to /v2/team with the
 *      query preserved → /v2/team auto-opens <JoinTeamModal>
 *      pre-filled with the code
 *    - Signed-out athletes land on the marketing landing page, which
 *      stashes the code (`joinStash`) before they sign in; the stash is
 *      consumed after the OAuth round-trip by <JoinCodeMount>, since the
 *      OAuth `redirectTo` returns to /v2/today and does NOT carry the
 *      query param back. (FIELD-TEST BUG 3.)
 *
 *  FIELD-TEST BUG 2 — the origin comes from `canonicalOrigin()`, NOT from
 *  `window.location.origin`. Inside the Capacitor shell that origin is
 *  `capacitor://localhost`, so the coach's "Copy link" was handing athletes
 *  a URL that resolves nowhere off his own phone. Web is unchanged (the
 *  deployed origin still wins there).
 *
 *  Examples:
 *    buildInviteLink("TEST1-XCAV")   [web, prod]
 *      → "https://tayro.run/v2/landing?join=TEST1-XCAV"
 *    buildInviteLink("TEST1-XCAV")   [native shell]
 *      → "https://tayro.run/v2/landing?join=TEST1-XCAV"  (never capacitor://)
 *
 *    buildInviteLink("")  → ""    (no code · render the "no invite" state)
 */
export function buildInviteLink(code: string | null | undefined): string {
  const clean = (code ?? '').trim();
  if (!clean) return '';
  return `${canonicalOrigin()}/v2/landing?join=${encodeURIComponent(clean)}`;
}
